export const jq = (obj: unknown, path: string): unknown => {
  if (!path) return undefined;

  // Split path and transforms (e.g. "items[].title | join(', ')")
  const parts = path.split('|').map((s) => s.trim());
  const dataPath = parts[0];
  const transforms = parts.slice(1);

  // Normalize [0] to .0 and [] to .*
  // We use * as a special segment for array mapping
  const normalized = dataPath.replace(/\[(\d+)\]/g, '.$1').replace(/\[\]/g, '.*');

  const segments = normalized.split('.').filter(Boolean);

  const traverse = (current: unknown, idx: number): unknown => {
    if (current === null || current === undefined) return undefined;

    // Auto-parse JSON strings if we are trying to traverse into them
    if (
      typeof current === 'string' &&
      (current.trim().startsWith('{') || current.trim().startsWith('['))
    ) {
      try {
        const parsed = JSON.parse(current) as unknown;
        return traverse(parsed, idx);
      } catch {
        // ignore parse error and treat as string
      }
    }

    if (idx === segments.length) return current;

    const key = segments[idx];

    if (key === '*') {
      if (Array.isArray(current)) {
        // Map over the array and continue traversal for each item
        const results = current.map((item) => traverse(item, idx + 1));
        // Flatten the results to match jq behavior (stream of values)
        return results.flat().filter((r) => r !== undefined);
      }
      return undefined;
    }

    if (typeof current !== 'object') return undefined;
    return traverse((current as Record<string, unknown>)[key], idx + 1);
  };

  let result = traverse(obj, 0);

  // Apply transforms
  if (transforms.length > 0) {
    result = transforms.reduce<unknown>((acc, transformStr) => {
      const { name, params } = parseTransform(transformStr);

      if (name === 'join') {
        const separator = params[0] || '';
        return Array.isArray(acc) ? acc.join(separator) : acc;
      }

      if (name === 'trim') {
        if (typeof acc === 'string') {
          return acc.trim();
        }
        if (Array.isArray(acc)) {
          return acc.map((v) => (typeof v === 'string' ? v.trim() : v));
        }
        return acc;
      }

      // Empty name arises from a structural artefact like a
      // trailing pipe (`'foo |'`) or double-pipe (`'foo ||'`)
      // in the schema path — not a typo. Silently pass through
      // so rendering a schema with harmless whitespace does not
      // spam the console N× per row.
      if (!name) {
        return acc;
      }

      // Genuine typo in the transform name — pass through with a
      // dev-visible warn. A `throw` here would fail-fast on
      // schema typos (e.g. the author wrote `| tirm` instead of
      // `| trim`) but would tear down the whole page via
      // `ResourceErrorBoundary` for a single mis-named cell.
      // The warn makes the miss observable in DevTools without
      // breaking production rendering when an unrecognised
      // transform ships.
      console.warn(
        `[jq] Unknown transform "${name}"; value passed through unchanged. ` + `Known: trim, join`,
      );
      return acc;
    }, result);
  }

  return result;
};

// Internal: exercised through `jq(..., '... | fn(arg)')` — do not re-export.
const parseTransform = (input: string): { name: string; params: string[] } => {
  const match = input.match(/^(\w+)(?:\((.*)\))?$/);
  if (!match) return { name: input, params: [] };

  const name = match[1];
  const argsStr = match[2];

  if (argsStr === undefined) return { name, params: [] };
  if (argsStr.trim() === '') return { name, params: [] };

  const params: string[] = [];
  let current = '';
  let inQuote: string | null = null;

  for (let i = 0; i < argsStr.length; i++) {
    const char = argsStr[i];
    if (inQuote) {
      if (char === inQuote) {
        inQuote = null;
      }
      current += char;
    } else {
      if (char === '"' || char === "'") {
        inQuote = char;
        current += char;
      } else if (char === ',') {
        params.push(current.trim());
        current = '';
        continue;
      } else {
        current += char;
      }
    }
  }
  if (current) params.push(current.trim());

  return {
    name,
    params: params.map((p) => {
      const quoteMatch = p.match(/^(['"])(.*)\1$/);
      return quoteMatch ? quoteMatch[2] : p;
    }),
  };
};
